<template>
  <div class="h-100 weather-statistics-editor d-flex flex-column">

    <!--
    <div v-if="!allowStatistics">
      <div class="alert alert-warning" role="alert">
        <h4 class="alert-heading">Statistics are not enabled</h4>
        <p>
          Weather Statistics are only available to monthly or annual plan users during beta testing. 
          Free and Metered accounts do not have access. Please <a href="/weather-data-editions/" target="_blank" class="text-primary text-decoration-underline">upgrade</a> to use this feature.
        </p>
      </div>
    </div>
  -->
    <div v-if="!queryDefinition.goal || queryDefinition.goal === 'timeline'">
   

      <h3 class="mb-2">Select a Statistics Type</h3>
      <p class="lead">Choose the type of weather statistics you want to analyze.</p>
      <p v-if="dateRangeCommentary" class="text-danger">{{ dateRangeCommentary }}</p>



      <div class="row justify-content-center g-4" :class="{ 'opacity-25 pe-none': !allowStatistics }">
        <div class="col-md-3">
          <div class="card h-100 shadow-sm" @click="selectGoal('statisticalsummary')" role="button">
            <div class="card-body">
              <h5 class="card-title">📊 Statistical Summary</h5>
              <p class="card-text">
                Calculate aggregates such as maximum temperature or total precipitation over time periods like months or years.
              </p>
            </div>
          </div>
        </div>

        <div class="col-md-3">
          <div class="card h-100 shadow-sm" @click="selectGoal('frostdates')" role="button">
            <div class="card-body">
              <h5 class="card-title">❄️ Frost Dates</h5>
              <p class="card-text">
                Identify the first and last frost based on temperature thresholds, helpful for agriculture and planting.
              </p>
            </div>
          </div>
        </div>

        <div class="col-md-3 pe-none">
          <div class="card h-100 shadow-sm position-relative" @click="selectGoal('regionstats')" role="button" disabled="true">
            <span class="position-absolute top-0 end-0 badge bg-warning text-dark px-3 py-2 rounded-0 rounded-bottom-start">
              Coming Soon
            </span>
            <div class="card-body ">
              <h5 class="card-title">🔍 Filtered Dataset</h5>
              <p class="card-text  opacity-25">
                Filter large datasets by weather conditions like temperature or precipitation to identify values below or above thresholds.
              </p>
            </div>
          </div>
        </div>
        
        <div class="col-md-3 pe-none">
          <div class="card h-100 shadow-sm position-relative" @click="selectGoal('regionstats')" role="button" disabled="true">
            <span class="position-absolute top-0 end-0 badge bg-warning text-dark px-3 py-2 rounded-0 rounded-bottom-start">
              Coming Soon
            </span>
            <div class="card-body">
              <h5 class="card-title">🛣️ Route-based weather</h5>
              <p class="card-text opacity-25">
                Analyze weather conditions along travel routes and corridors for transportation planning and logistics optimization.
              </p>
            </div>
          </div>
        </div>
      </div>
     
    </div>

    <!-- Step 2: Editor UI for selected goal -->
  
    <component v-else-if="step==='createdefinition'" class="d-flex flex-column flex-grow-1" style="min-height: 0;"
      :is="currentEditor"
      :definition="queryDefinition"
      @update="updateStatsDefinition"
      @clearGoal="clearGoal"
    />

  </div>
</template>

<script>
import StatisticalSummaryEditor from './weatherstatistics/StatisticalSummaryEditor.vue'
import FrostDatesEditor from './weatherstatistics/FrostDatesEditor.vue'
import RegionStatsEditor from './weatherstatistics/RegionStatsEditor.vue'
//import {VcMetadataUtils} from "/src/visualcrossing-core/vcmetadatautils";
import {isValidWeatherStatisticsDefn } from './weatherstatistics/weatherstatisticsutils.js';
import {VcDateUtils} from '/src/visualcrossing-core/vcdateutils'; 

export default {
  name: 'WeatherStatisticsEditor',
  components: {
    StatisticalSummaryEditor,
    FrostDatesEditor,
    RegionStatsEditor
  },
  props: {
    sessionId:String,
    sessionState:Object,
    packageDefn: Object, 
    value: {
      type: Object,
      required: true
    }
  },
  emits: ['change:metadata', 'update:statsdefinition', 'update:packageDefn'], // Add new emits
  data() {
    return {
      localPackageDefn: this.createLocalPackageDefn(),
      step:'createdefinition',
      existingJobPackages:[],
    }
  },
  computed: {
    subscriptionParams() {
      return this.sessionState && this.sessionState.subscriptionParams;
    },
    allowStatistics:function() {
      return this.subscriptionParams && this.subscriptionParams.allowStatistics;
    },

    hasValidDefn() {
      return this.queryDefinition && isValidWeatherStatisticsDefn(this.queryDefinition);
    },

    currentEditor() {
      switch (this.queryDefinition.goal) {
        case 'statisticalsummary': return 'StatisticalSummaryEditor'
        case 'frostdates': return 'FrostDatesEditor'
        case 'regionstats': return 'RegionStatsEditor'
        default: {
          console.error('Unknown goal:', this.queryDefinition.goal)
          return null
        }
      }
    },
    queryDefinition() {
      return this.localPackageDefn && this.localPackageDefn.params;
    },

    statsDefinition() {
      try {
        return this.queryDefinition.statsdefinition ? JSON.parse(this.queryDefinition.statsdefinition) : {}
      } catch (e) {
        return {}
      }
    },

    locationCount() {
      return (this.localPackageDefn && this.localPackageDefn.locations && this.localPackageDefn.locations.length) || 0;
    },
    firstLocationText() {
      if (!this.localPackageDefn || !this.localPackageDefn.locations || this.localPackageDefn.locations.length === 0) {
        return '';
      }
      const firstLocation = this.localPackageDefn.locations[0];
      // Assuming locations have a 'name' or 'address' property, adjust as needed
      return firstLocation.name || firstLocation.address || firstLocation.location || 'Unknown location';
    },
    
    dateRange() {
      if (!this.localPackageDefn || !this.localPackageDefn.params) {
        return null;
      }
      const params = this.localPackageDefn.params;
      const startDate = params.startdate;
      const endDate = params.enddate;
      return VcDateUtils.resolveDateRange(startDate, endDate);
    },
    dateRangeText() {
      if (!this.dateRange) {
        return '-';
      }
      const [ startDate, endDate ] = this.dateRange;
      return VcDateUtils.resolveDateRangeText(startDate, endDate);
    },
    dateRangeCommentary() {
      if (!this.dateRange) {
        return 'Enter a date range to create a statistical summary.';
      }
      const [ startDate, endDate ] = this.dateRange;
      if (!startDate && !endDate) {
        return 'Verify your date range.';
      }
      
      // Check if date range is less than 365 days
      if (startDate && endDate) {
        const timeDifference = endDate.getTime() - startDate.getTime();
        const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
        
        if (daysDifference < 365) {
          return `Warning: Your date range is only ${daysDifference} days. Weather statistics work best with longer periods (1+ years).`;
        }
      }
      
      return null;
    }
  },
  methods: {

    createLocalPackageDefn() {
      var me=this;
      let newPackageDefn= me.packageDefn && JSON.parse(JSON.stringify(me.packageDefn));
      if (newPackageDefn) {
        // Initialize statsdefinition if it doesn't exist
        if (!newPackageDefn.params) {
          newPackageDefn.params = {};
        }
        if (!newPackageDefn.params.statsdefinition) {
          newPackageDefn.params.statsdefinition = "";
        }
      }
      return newPackageDefn;
    },

    selectGoal(goal) {
      const oldDefinition = this.queryDefinition.statsdefinition;
      
      this.queryDefinition.goal = goal;
      this.queryDefinition.statsdefinition = '';
      this.queryDefinition.include = 'days'; // Default include based on goal
      this.queryDefinition.contentType = 'csv'; // Ensure contentType is set
      // Emit update event
      this.emitStatsDefinitionUpdate(oldDefinition, '');
    },
    
    clearGoal() {
      const oldDefinition = this.queryDefinition.statsdefinition;
      
      this.queryDefinition.goal = 'timeline';
      this.queryDefinition.statsdefinition = '';
      
      // Emit update event
      this.emitStatsDefinitionUpdate(oldDefinition, '');
    },
    
    updateStatsDefinition(newDefinition) {
      const oldDefinition = this.queryDefinition.statsdefinition;
      const newDefinitionStr = JSON.stringify(newDefinition);
      
      this.queryDefinition.statsdefinition = newDefinitionStr;
      
      // Emit update event
      this.emitStatsDefinitionUpdate(oldDefinition, newDefinitionStr);
    },
    
    // New method to handle emitting the update event
    emitStatsDefinitionUpdate(oldValue, newValue) {
      // Only emit if the value actually changed
      if (oldValue !== newValue) {
        // Emit the raw statsdefinition change
        this.$emit('update:statsdefinition',  {
        
          statsDefn: newValue,
          goal: this.queryDefinition.goal,
          include: this.queryDefinition.include,
          contentType: this.queryDefinition.contentType
        });
        
        // Also emit the entire packageDefn for parent components that need it
        this.$emit('update:packageDefn', this.localPackageDefn);
      }
    },
    

    getExistingPackageByName(name) {
      var me=this;
      if (!me.existingJobPackages) return null;
      name=(name && name.toLowerCase());
      return me.existingJobPackages.reduce(function(a,d) {
        return a || (d && d.name && d.name.toLowerCase() === name && d);
      }, null);
    },
    

  },
  watch: {
  
  },
 
}
</script>

<style scoped>

</style>
